// frontend/src/components/expenses/Expenses.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../../api';
import { fmt, fmtK, PayBadge, StatCard, Pager, CollapsibleCard, today, toDate } from '../common/CommonComponents';
import { getCachedExpenses, cacheExpenses } from '../../services/cacheService';

function Expenses({ showAlert, user }) {
  const cats = ["Labour/Staff Cost","Transport/Logistics","Generator Fuel","Electricity","Shop Reserve","Repairs & Maintenance","Ambassador Payout","Other"];
  const blank = { expenseId:"", category:cats[0], description:"", amount:"", date:today(), ambassadorEmail:"", shopId:"" };
  const [form,setForm]         = useState(blank);
  const [mode,setMode]         = useState("create");
  const [expenses,setExpenses] = useState([]);
  const [total,setTotal]       = useState(0);
  const [overallTotal, setOverallTotal] = useState(0);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [approvedTotal, setApprovedTotal] = useState(0);
  const [page,setPage]         = useState(1);
  const [search,setSearch]     = useState("");
  const [statusF,setStatusF]   = useState("");
  const [categoryF,setCategoryF] = useState("");
  const [ambassadors,setAmbassadors] = useState([]);
  const [loading,setLoading]   = useState(false);
  const [submitting,setSubmitting] = useState(false);
  const submitLock = useRef(false);
  const expenseOperationId = useRef(null);
  const [selectedAmb,setSelectedAmb] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [shops, setShops] = useState([]);

  const isManager = user.role === "manager";
  const isCashier = user.role === "cashier";
  const canApproveExpenses = !!user.isBusinessOwner || (isManager && !user.shopId);
  const requiresCentralApproval = !canApproveExpenses;
  const canChooseExpenseShop = !!user.isBusinessOwner || (isManager && !user.shopId);
  const shopNameById = Object.fromEntries(shops.map(s => [s.shopId, s.name]));

  const load = useCallback(async () => {
    setLoading(true);
    
    // If offline, use cached data
    if (!navigator.onLine) {
      const cached = getCachedExpenses();
      if (cached) {
        setExpenses(cached.expenses || []);
        setTotal(cached.total || 0);
        setOverallTotal(cached.overallTotal || 0);
        setPendingTotal(cached.pendingTotal || 0);
        setApprovedTotal(cached.approvedTotal || 0);
        setLoading(false);
        showAlert("You are offline. Showing cached expenses.", "info");
        return;
      }
    }
    
    // If online, fetch from server
    try {
      const [allRes, pageRes] = await Promise.all([
        api.getExpenses({ search, status: statusF, category: categoryF, page: 1, limit: 10000, dateFrom, dateTo }),
        api.getExpenses({ search, status: statusF, category: categoryF, page, limit: 20, dateFrom, dateTo })
      ]);
      
      const allExpenses = allRes.expenses || [];
      setExpenses(pageRes.expenses || []);
      setTotal(allRes.total || 0);
      
      // Calculate totals from ALL filtered expenses
      const totalAmount = allExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      const pending = allExpenses.filter(e => e.status === "Pending").reduce((sum, e) => sum + (e.amount || 0), 0);
      const approved = allExpenses.filter(e => e.status === "Approved").reduce((sum, e) => sum + (e.amount || 0), 0);
      
      setOverallTotal(totalAmount);
      setPendingTotal(pending);
      setApprovedTotal(approved);
      
      // Save to cache
      cacheExpenses({
        expenses: pageRes.expenses || [],
        total: allRes.total || 0,
        overallTotal: totalAmount,
        pendingTotal: pending,
        approvedTotal: approved
      });
      
    } catch(e) {
      showAlert("Failed to load expenses", "error");
    } finally {
      setLoading(false);
    }
  }, [search, statusF, categoryF, page, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.getAmbassadorList().then(setAmbassadors).catch(()=>{}); }, []);
  useEffect(() => {
    if (canChooseExpenseShop) api.getShops().then(setShops).catch(()=>setShops([]));
  }, [canChooseExpenseShop]);

  const setFormField = (k,v) => setForm(f => ({...f, [k]:v}));

  const resetForm = () => {
    expenseOperationId.current = null;
    setForm(blank);
    setMode("create");
    setSelectedAmb(null);
    setShowForm(false);
  };

  const handleAmbSelect = (email) => {
    setFormField("ambassadorEmail", email);
    if (!email) { setSelectedAmb(null); setFormField("amount", ""); return; }
    const amb = ambassadors.find(a => a.email === email);
    if (amb) { 
      setSelectedAmb(amb); 
      setFormField("amount", amb.availableBalance > 0 ? amb.availableBalance.toFixed(2) : ""); 
      setFormField("description", `Ambassador payout — ${amb.name || email}`);
    }
  };

  const submit = async () => {
    if (submitLock.current) return;
    if (!form.description || !form.amount) {
      showAlert("Fill all required fields", "error");
      return;
    }
    if (form.category === "Ambassador Payout") {
      if (!form.ambassadorEmail) {
        showAlert("Select an ambassador", "error");
        return;
      }
      if (selectedAmb && Number(form.amount) > selectedAmb.availableBalance) {
        showAlert(`Amount exceeds available balance of ₦${selectedAmb.availableBalance.toLocaleString("en-NG", {minimumFractionDigits:2})}`, "error");
        return;
      }
    }
    submitLock.current = true;
    setSubmitting(true);
    try {
      if (mode === "create") {
        if (!expenseOperationId.current) expenseOperationId.current = window.crypto?.randomUUID?.() || ("expense-" + Date.now() + "-" + Math.random().toString(36).slice(2));
        await api.createExpense({
          category: form.category,
          description: form.description,
          amount: Number(form.amount),
          date: form.date,
          ambassadorEmail: form.category === "Ambassador Payout" ? form.ambassadorEmail : null,
          shopId: canChooseExpenseShop ? (form.shopId || null) : undefined
        }, expenseOperationId.current);
        showAlert(requiresCentralApproval ? "Expense submitted for Central approval." : "Expense recorded!");
      } else {
        await api.updateExpense(form.expenseId, {
          category: form.category,
          description: form.description,
          amount: Number(form.amount),
          date: form.date,
          shopId: canChooseExpenseShop ? (form.shopId || null) : undefined
        });
        showAlert("Expense updated!");
      }
      submitLock.current = false;
      resetForm();
      load();
    } catch(e) {
      showAlert(e.message, "error");
      if (e.code === "MUTATION_OUTCOME_UNKNOWN") load();
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const handleApprove = async (id, action) => {
    try {
      await api.approveExpense(id, action);
      showAlert(action === "approve" ? "Expense approved!" : "Expense rejected!");
      api.getAmbassadorList().then(setAmbassadors).catch(()=>{});
      load();
    } catch(e) {
      showAlert(e.message, "error");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setStatusF("");
    setCategoryF("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const getStatusStyle = (status) => {
    switch(status) {
      case "Approved": return { bg: "#10B98120", color: "#10B981", icon: "✅" };
      case "Pending": return { bg: "#F59E0B20", color: "#F59E0B", icon: "⏳" };
      case "Rejected": return { bg: "#EF444420", color: "#EF4444", icon: "❌" };
      default: return { bg: "#94A3B820", color: "#94A3B8", icon: "📋" };
    }
  };

  // Calculate counts from the current page expenses (for display in cards)
  const pendingCount = expenses.filter(e => e.status === "Pending").length;
  const approvedCount = expenses.filter(e => e.status === "Approved").length;

  return (
    <div>
      {/* Header Section */}
      <div className="section-header">
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>🧾 Expenses</h2>
          <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>
            {canApproveExpenses
              ? "Manage business expenses, approve pending requests, and track spending."
              : "Submit shop expenses for Central approval and track their status."}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          + New Expense
        </button>
      </div>

      {/* Collapsible Expense Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 20, border: mode === "update" ? "2px solid #F59E0B" : "2px solid var(--accent)" }}>
          <div className="section-header">
            <div>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                {mode === "create" ? "➕ New Expense" : "✏️ Update Expense"}
              </h2>
              {mode === "update" && <span className="mode-tag">UPDATE MODE</span>}
              {requiresCentralApproval && mode === "create" && <span className="pending-badge">Requires Central Approval</span>}
            </div>
            <button className="btn btn-secondary" onClick={resetForm} disabled={submitting}>✕ Cancel</button>
          </div>

          {canChooseExpenseShop && (
            <div className="form-group">
              <label>Expense Location</label>
              <select value={form.shopId || ""} onChange={e => setFormField("shopId", e.target.value)}>
                <option value="">Central / Unallocated</option>
                {shops.filter(s => s.active).map(s => (
                  <option key={s.shopId} value={s.shopId}>{s.name}{s.state ? ` — ${s.state}` : ""}</option>
                ))}
              </select>
              <div style={{fontSize:10.5,color:"var(--text-3)",marginTop:4}}>
                Choose the shop that incurred this expense. Leave as Central for head-office or business-wide costs.
              </div>
            </div>
          )}
          {!canChooseExpenseShop && user.shopId && (
            <div className="alert alert-info" style={{fontSize:11.5}}>
              This expense will be attributed automatically to your assigned shop.
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <select value={form.category} onChange={e => {
                setFormField("category", e.target.value);
                if (e.target.value !== "Ambassador Payout") {
                  setFormField("ambassadorEmail", "");
                  setSelectedAmb(null);
                }
              }}>
                {cats.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Date</label>
              <input type="date" value={form.date} onChange={e => setFormField("date", e.target.value)} />
            </div>
          </div>

          {form.category === "Ambassador Payout" && (
            <div className="form-group">
              <label>Select Ambassador *</label>
              <select value={form.ambassadorEmail} onChange={e => handleAmbSelect(e.target.value)}>
                <option value="">— Select Ambassador —</option>
                {ambassadors.map(a => (
                  <option key={a.email} value={a.email}>
                    {a.name || a.email} — Available: ₦{(a.availableBalance || 0).toLocaleString("en-NG", {minimumFractionDigits: 2})}
                  </option>
                ))}
              </select>
              {selectedAmb && (
                <div className="alert alert-info" style={{ marginTop: 8, fontSize: 12 }}>
                  Available to pay out: <strong>₦{(selectedAmb.availableBalance || 0).toLocaleString("en-NG", {minimumFractionDigits: 2})}</strong>
                  {selectedAmb.availableBalance <= 0 && <span style={{ color: "#ef4444", marginLeft: 8 }}>— No available balance</span>}
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Description *</label>
            <input value={form.description} onChange={e => setFormField("description", e.target.value)} placeholder="What was this expense for?" />
          </div>

          <div className="form-group">
            <label>
              Amount (₦) *
              {form.category === "Ambassador Payout" && selectedAmb && (
                <span style={{ color: "#6b7280", fontWeight: 400, marginLeft: 8 }}>
                  max: ₦{(selectedAmb.availableBalance || 0).toLocaleString("en-NG", {minimumFractionDigits: 2})}
                </span>
              )}
            </label>
            <input 
              type="number" 
              value={form.amount} 
              onChange={e => setFormField("amount", e.target.value)} 
              max={form.category === "Ambassador Payout" && selectedAmb ? selectedAmb.availableBalance : undefined}
            />
            {form.category === "Ambassador Payout" && selectedAmb && Number(form.amount) > selectedAmb.availableBalance && (
              <p style={{ color: "#ef4444", fontSize: 11, marginTop: 4 }}>⚠️ Amount exceeds available balance</p>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary" onClick={submit} disabled={submitting}>
              {submitting ? "Processing…" : mode === "create" ? "➕ Add Expense" : "💾 Update Expense"}
            </button>
            <button className="btn btn-secondary" onClick={resetForm} disabled={submitting}>Cancel</button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card" style={{ borderLeft: "4px solid #3B82F6" }}>
          <div className="lbl">Total Expenses</div>
          <div className="val" style={{ fontSize: 20 }}>{total}</div>
          <div className="stat-sub">Total records</div>
        </div>
        <div className="stat-card" style={{ borderLeft: "4px solid #10B981" }}>
          <div className="lbl">Total Amount</div>
          <div className="val success">{fmt(overallTotal)}</div>
          <div className="stat-sub">Sum of all filtered expenses</div>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card" style={{ borderLeft: "4px solid #F59E0B" }}>
          <div className="lbl">Pending Amount</div>
          <div className="val warning">{fmt(pendingTotal)}</div>
          <div className="stat-sub">{pendingCount} transaction{pendingCount !== 1 ? "s" : ""} awaiting approval</div>
        </div>
        <div className="stat-card" style={{ borderLeft: "4px solid #10B981" }}>
          <div className="lbl">Approved Amount</div>
          <div className="val success">{fmt(approvedTotal)}</div>
          <div className="stat-sub">Completed expenses</div>
        </div>
      </div>

      {/* Expenses List */}
      <div className="card">
        <div className="section-header">
          <h2>📋 Expense History ({total})</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-sm btn-secondary" onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? "▲ Hide Filters" : "▼ Show Filters"}
            </button>
            {isManager && (
              <button className="btn-sm btn-edit" onClick={api.exportExpenses}>⬇ CSV</button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="search-bar">
          <input 
            placeholder="🔍 Search by description..." 
            value={search} 
            onChange={e => { setSearch(e.target.value); setPage(1); }} 
          />
          <select value={statusF} onChange={e => { setStatusF(e.target.value); setPage(1); }}>
            <option value="">All Status</option>
            <option value="Approved">✅ Approved</option>
            <option value="Pending">⏳ Pending</option>
            <option value="Rejected">❌ Rejected</option>
          </select>
          <select value={categoryF} onChange={e => { setCategoryF(e.target.value); setPage(1); }}>
            <option value="">All Categories</option>
            {cats.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        {showFilters && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <div className="form-group" style={{ flex: 1, minWidth: 130, margin: 0 }}>
              <label style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}>From Date</label>
              <input 
                type="date" 
                value={dateFrom} 
                onChange={e => { setDateFrom(e.target.value); setPage(1); }} 
              />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: 130, margin: 0 }}>
              <label style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}>To Date</label>
              <input 
                type="date" 
                value={dateTo} 
                onChange={e => { setDateTo(e.target.value); setPage(1); }} 
              />
            </div>
            {(search || statusF || categoryF || dateFrom || dateTo) && (
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button className="btn-sm btn-secondary" onClick={resetFilters}>
                  ✕ Clear Filters
                </button>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="spinner">Loading...</div>
        ) : (
          <div className="table-wrap">
            <table className="w-full">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>By</th>
                  {isManager && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 && (
                  <tr>
                    <td colSpan={isManager ? 9 : 8} style={{ textAlign: "center", color: "var(--text-3)", padding: 24 }}>
                      No expenses found
                    </td>
                  </tr>
                )}
                {expenses.map(e => {
                  const statusStyle = getStatusStyle(e.status);
                  return (
                    <tr key={e.expenseId}>
                      <td style={{ fontFamily: "monospace", fontSize: 11 }}>{e.expenseId}</td>
                      <td style={{ fontSize: 12 }}>{e.category}</td>
                      <td style={{ maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" }}>{e.description}</td>
                      <td style={{ fontWeight: 700, color: "#EF4444" }}>{fmt(e.amount)}</td>
                      <td style={{ fontSize: 11 }}>{toDate(e.date)}</td>
                      <td style={{ fontSize: 11 }}>{e.shopId ? (shopNameById[e.shopId] || "Assigned shop") : "Central / Unallocated"}</td>
                      <td>
                        <span style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "2px 8px",
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 700,
                          background: statusStyle.bg,
                          color: statusStyle.color
                        }}>
                          {statusStyle.icon} {e.status}
                        </span>
                       </td>
                      <td style={{ fontSize: 11 }}>{e.loggedBy}</td>
                      {isManager && (
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            {canApproveExpenses && e.status === "Pending" && (
                              <>
                                <button className="btn-sm btn-approve" onClick={() => handleApprove(e.expenseId, "approve")}>✓</button>
                                <button className="btn-sm btn-reject" onClick={() => handleApprove(e.expenseId, "reject")}>✕</button>
                              </>
                            )}
                            {(canApproveExpenses ? e.status !== "Rejected" : (e.status === "Pending" && e.loggedBy === user.email)) && (
                              <button 
                                className="btn-sm btn-edit" 
                                onClick={() => {
                                  setForm({
                                    expenseId: e.expenseId,
                                    category: e.category,
                                    description: e.description,
                                    amount: e.amount,
                                    date: toDate(e.date),
                                    ambassadorEmail: e.ambassadorEmail || "",
                                    shopId: e.shopId || ""
                                  });
                                  setMode("update");
                                  setShowForm(true);
                                  window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pager page={page} total={total} per={20} onChange={setPage} />
      </div>

      {/* Cashier Info Alert */}
      {requiresCentralApproval && (
        <div className="alert alert-info" style={{ marginTop: 16 }}>
          📋 <strong>Approval control:</strong> Your expense requests remain Pending and do not affect cash or profit until the Owner or Central Manager approves them.
        </div>
      )}
    </div>
  );
}

export default Expenses;